// src/lib/themeTokens.js
//
// Death Card Design System — Zone Theme Tokens
// Source: claude.design HTML exports (extracted 2026-04-28)
//
// Four zones:
//   RECON   — Player gameplay. "You're in the fight."
//   DOSSIER — Intel/bounty details. "You're reading classified intel."
//   ARMOR   — Team/barracks. "You're gearing up."
//   CIPHER  — GM/admin console. "You're hacking the system."
//
// Principle: "Each theme should tell you where you are
// and what you are touching without having to think about it."

export const FONT_IMPORTS = {
  recon: 'https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap',
  dossier: 'https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap',
  armor: 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700&family=Saira+Condensed:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap',
  cipher: 'https://fonts.googleapis.com/css2?family=VT323&family=Share+Tech+Mono&family=JetBrains+Mono:wght@400;500;700&display=swap',
}

export const THEMES = {
  // ── RECON — Modern tactical HUD ──────────────────────────
  // Dark olive, bracketed frames, soft cyan/lime ACCENTS on
  // warm off-white text. Green is accent-only, never body text.
  recon: {
    id: 'recon',
    label: 'Field HUD',
    description: "You're in the fight.",

    '--dc-bg':           '#14181a',
    '--dc-bg-gradient':  'radial-gradient(ellipse at 20% 10%, rgba(40,70,50,.6), transparent 60%), radial-gradient(ellipse at 80% 90%, rgba(60,80,50,.4), transparent 60%), #14181a',
    '--dc-surface':      'rgba(28, 38, 38, .6)',
    '--dc-surface-solid': '#1c2626',   // fallback when transparency won't work
    '--dc-surface-alt':  '#1a2024',
    '--dc-border':       'rgba(140, 180, 130, .2)',
    '--dc-primary':      '#9adb6e',    // LIME — accent only, NOT body text
    '--dc-secondary':    '#4dd6c2',    // CYAN — actions, interactive
    '--dc-accent':       '#9adb6e',
    '--dc-alert':        '#ff6a3d',    // WARN — hostile/danger
    '--dc-text':         '#e0e8de',    // WARM OFF-WHITE — primary text
    '--dc-text-muted':   '#8aa090',    // SAGE — secondary text/labels
    '--dc-text-faded':   '#5a6a60',    // DIM SAGE — tertiary/disabled
    '--dc-badge-bg':     'rgba(154, 219, 110, 0.08)',
    '--dc-badge-text':   '#9adb6e',
    '--dc-badge-border': 'rgba(154, 219, 110, 0.4)',
    '--dc-rule':         'rgba(154, 219, 110, .14)',

    '--dc-font-display': "'Rajdhani', 'Saira Condensed', sans-serif",
    '--dc-font-body':    "'Inter', system-ui, sans-serif",
    '--dc-font-mono':    "'JetBrains Mono', monospace",
    '--dc-text-transform': 'uppercase',
    '--dc-letter-spacing': '0.14em',

    '--dc-glow':         '0 0 8px rgba(154, 219, 110, .5)',
    '--dc-shadow':       '0 2px 8px rgba(0, 0, 0, 0.3)',
    '--dc-radius':       '0px',
    '--dc-backdrop':     'blur(6px)',
  },

  // ── DOSSIER — Declassified field-ops folder ──────────────
  // Manila card stock, typewriter fonts, classified stamps,
  // paper textures. Warm analog.
  dossier: {
    id: 'dossier',
    label: 'Archive Branch',
    description: "You're reading classified intel.",

    '--dc-bg':           '#d8c898',
    '--dc-bg-gradient':  '#d8c898',
    '--dc-surface':      '#f0e6c8',    // PAPER
    '--dc-surface-solid': '#f0e6c8',
    '--dc-surface-alt':  '#e8dec3',    // PAPER-2
    '--dc-border':       'rgba(120, 100, 60, .35)',
    '--dc-primary':      '#1f1d18',    // INK
    '--dc-secondary':    '#4a4636',    // INK-2
    '--dc-accent':       '#a82a1f',    // STAMP RED
    '--dc-accent-2':     '#2a4a8c',    // STAMP BLUE
    '--dc-alert':        '#a82a1f',
    '--dc-text':         '#1f1d18',
    '--dc-text-muted':   '#4a4636',
    '--dc-text-faded':   '#7a7058',
    '--dc-badge-bg':     'rgba(168, 42, 31, 0.12)',
    '--dc-badge-text':   '#a82a1f',
    '--dc-rule':         'rgba(31, 29, 24, .22)',

    '--dc-manila':       '#f0e6c8',
    '--dc-manila-dark':  '#cbb98c',
    '--dc-manila-light': '#e8dec3',
    '--dc-ink':          '#1f1d18',
    '--dc-stamp-red':    '#a82a1f',
    '--dc-redact':       '#1a1a1a',
    '--dc-tab':          '#cbb98c',

    '--dc-font-display': "'Stardos Stencil', 'Special Elite', serif",
    '--dc-font-body':    "'Special Elite', 'Courier Prime', monospace",
    '--dc-font-mono':    "'Courier Prime', monospace",
    '--dc-text-transform': 'uppercase',
    '--dc-letter-spacing': '0.14em',

    '--dc-glow':         'none',
    '--dc-shadow':       '0 2px 8px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0,0,0,.12)',
    '--dc-radius':       '0px',
    '--dc-backdrop':     'none',
  },

  // ── ARMOR — Ruggedized-tablet sci-fi ─────────────────────
  // Graphite chamfered shell, vivid orange power accents,
  // cyan holographic rings. Equipment-grade.
  armor: {
    id: 'armor',
    label: 'Barracks',
    description: "You're gearing up.",

    '--dc-bg':           '#0e1116',
    '--dc-bg-gradient':  'radial-gradient(ellipse at 30% 20%, rgba(20,40,60,.5), transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(30,20,40,.4), transparent 60%), #0e1116',
    '--dc-surface':      '#2a2e36',    // SHELL
    '--dc-surface-solid': '#2a2e36',
    '--dc-surface-alt':  '#1a1d22',    // SHELL-2
    '--dc-border':       '#4a5058',    // SHELL-EDGE
    '--dc-primary':      '#ff7a18',    // POWER (orange)
    '--dc-secondary':    '#4dd6ff',    // HOLO (cyan)
    '--dc-accent':       '#aef0ff',    // HOLO-2 (light cyan)
    '--dc-alert':        '#ff4136',
    '--dc-text':         '#eaf2fb',    // light steel
    '--dc-text-muted':   '#9aa6b0',
    '--dc-text-faded':   '#5a6470',
    '--dc-badge-bg':     'rgba(77, 214, 255, 0.08)',
    '--dc-badge-text':   '#4dd6ff',
    '--dc-badge-border': 'rgba(77, 214, 255, .4)',
    '--dc-rule':         'rgba(77, 214, 255, .18)',

    '--dc-font-display': "'Orbitron', 'Rajdhani', sans-serif",
    '--dc-font-body':    "'Saira Condensed', 'Rajdhani', sans-serif",
    '--dc-font-mono':    "'IBM Plex Mono', monospace",
    '--dc-text-transform': 'uppercase',
    '--dc-letter-spacing': '0.12em',

    '--dc-glow':         '0 0 12px rgba(255, 122, 24, .6)',
    '--dc-shadow':       '0 2px 10px rgba(0, 0, 0, 0.4)',
    '--dc-radius':       '0px',
    '--dc-backdrop':     'none',
  },

  // ── CIPHER — Movie-style hacker terminal ─────────────────
  // Phosphor green on black, scanlines, CRT glow.
  // Pure command-line aesthetic.
  cipher: {
    id: 'cipher',
    label: 'Terminal',
    description: "You're hacking the system.",

    '--dc-bg':           '#000000',
    '--dc-bg-gradient':  '#000000',
    '--dc-surface':      '#0a140a',
    '--dc-surface-solid': '#0a140a',
    '--dc-surface-alt':  '#050a05',
    '--dc-border':       'rgba(0, 255, 102, .18)',
    '--dc-primary':      '#00ff66',    // PHOSPHOR
    '--dc-secondary':    '#9aff9a',    // GLOW
    '--dc-accent':       '#00ff66',
    '--dc-alert':        '#ff2a2a',
    '--dc-amber':        '#ffcc33',
    '--dc-text':         '#00ff66',    // green IS the text for cipher
    '--dc-text-muted':   '#4aaa5a',
    '--dc-text-faded':   '#2a6a3a',
    '--dc-badge-bg':     'rgba(0, 255, 102, 0.08)',
    '--dc-badge-text':   '#00ff66',
    '--dc-rule':         'rgba(0, 255, 102, .18)',

    '--dc-font-display': "'VT323', 'Share Tech Mono', monospace",
    '--dc-font-body':    "'JetBrains Mono', monospace",
    '--dc-font-mono':    "'JetBrains Mono', monospace",
    '--dc-text-transform': 'uppercase',
    '--dc-letter-spacing': '0.08em',

    '--dc-glow':         '0 0 6px rgba(0, 255, 102, .5)',
    '--dc-shadow':       '0 0 12px rgba(0, 255, 102, .15), inset 0 0 20px rgba(0, 255, 102, .04)',
    '--dc-radius':       '0px',
    '--dc-backdrop':     'none',
    '--dc-text-shadow':  '0 0 4px rgba(0, 255, 102, .5)',
  },
}

export const ROUTE_ZONES = {
  '/play': 'recon',
  '/scan': 'recon',
  '/down': 'recon',
  '/bounty': 'dossier',
  '/bounties': 'dossier',
  '/dossier': 'dossier',
  '/aar': 'dossier',
  '/stats': 'dossier',
  '/teams': 'armor',
  '/team': 'armor',
  '/cc': 'armor',
  '/loadout': 'armor',
  '/join': 'armor',
  '/register': 'armor',
  '/profile': 'armor',
  '/dashboard': 'cipher',
  '/admin': 'cipher',
  '/org': 'cipher',
  '/settings': 'cipher',
  '/event/create': 'cipher',
  '/event/edit': 'cipher',
  '/bounty/create': 'cipher',
  '/bounty/edit': 'cipher',
}

export function resolveZone(pathname) {
  if (ROUTE_ZONES[pathname]) return ROUTE_ZONES[pathname]
  const matches = Object.entries(ROUTE_ZONES)
    .filter(([route]) => pathname.startsWith(route))
    .sort((a, b) => b[0].length - a[0].length)
  return matches[0]?.[1] || 'recon'
}

export default THEMES
